import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Boxes, Store, GraduationCap, Newspaper, Receipt, Settings, ShieldCheck, LogOut, Package, Menu, X } from 'lucide-react';
import { SiteHeader } from '../components/platform/SiteLayout';
import { Container, Card, Heading, Btn, CardSkeletons, EmptyState, ErrorState, StatusBadge, Spinner } from '../components/platform/ui';
import ProductCard from '../components/platform/ProductCard';
import useAsync from '../hooks/useAsync';
import { platformApi } from '../services/platformApi';
import { subscriptionAPI } from '../utils/auth';
import { useAuth } from '../contexts/AuthContext';
import { LLink, LNavLink, useLang } from '../lib/lang';
import { formatDate, formatDateTime, formatPrice, plural } from '../lib/pay';

const canOpenAdmin = (user) => !!(user && (user.is_admin || (user.permissions || []).length > 0));

const Sidebar = ({ user, onNavigate }) => {
  const { t } = useTranslation();
  const nav = [
    { to: '/cabinet', label: t('pl.cabinet.nav.home'), icon: Home, end: true },
    { to: '/cabinet/products', label: t('pl.cabinet.nav.products'), icon: Boxes },
    { to: '/cabinet/catalog', label: t('pl.cabinet.nav.catalog'), icon: Store },
    { to: '/cabinet/courses', label: t('pl.cabinet.nav.courses'), icon: GraduationCap },
    { to: '/cabinet/news', label: t('pl.cabinet.nav.news'), icon: Newspaper },
    { to: '/cabinet/purchases', label: t('pl.cabinet.nav.purchases'), icon: Receipt },
    { to: '/cabinet/settings', label: t('pl.cabinet.nav.settings'), icon: Settings },
  ];
  return (
    <nav className="flex flex-col gap-1" aria-label={t('pl.cabinet.nav.sections')}>
      {nav.map(({ to, label, icon: Icon, end }) => (
        <LNavLink key={to} to={to} end={end} onClick={onNavigate}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 border-2 ${isActive ? 'bg-black text-white border-black' : 'border-transparent hover:border-black'}`}>
          <Icon size={18} />{label}
        </LNavLink>
      ))}
      {canOpenAdmin(user) && (
        <LNavLink to="/admin" onClick={onNavigate} className="flex items-center gap-3 px-3 py-2.5 border-2 border-transparent hover:border-black mt-4 text-[#C41E3A]">
          <ShieldCheck size={18} />{t('pl.cabinet.nav.admin')}
        </LNavLink>
      )}
    </nav>
  );
};

/* ---------- Разделы ---------- */

const ProductsGrid = ({ products, emptyTitle, emptyText }) => {
  const { t } = useTranslation();
  return products.length === 0
    ? <EmptyState icon={Package} title={emptyTitle || t('pl.cabinet.myProductsEmpty')} text={emptyText} action={<Btn to="/cabinet/catalog">{t('pl.cabinet.openCatalog')}</Btn>} />
    : <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">{products.map((p) => <ProductCard key={p.id} product={p} />)}</div>;
};

const CabinetHome = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const { lang } = useLang();
  const prods = useAsync(platformApi.products, [i18n.language]);
  const news = useAsync(platformApi.news, [i18n.language]);
  const available = (prods.data || []).filter((p) => p.access?.state === 'granted');
  const tokens = user?.tokens ?? 0;
  return (
    <div className="space-y-10">
      <div>
        <Heading level={1} className="text-3xl sm:text-4xl mb-2">{t('pl.cabinet.hello', { name: user?.name })}</Heading>
        <p className="text-gray-700">{t('pl.cabinet.helloText')}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <p className="text-sm text-gray-600">{t('pl.cabinet.balance')}</p>
          <p className="text-4xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>{user?.is_admin ? '∞' : tokens}</p>
          {!user?.is_admin && <LLink to="/products/cbct-segmentation#tariffs" className="text-[#C41E3A] underline text-sm">{t('pl.cabinet.topUp')}</LLink>}
        </Card>
        <Card>
          <p className="text-sm text-gray-600">{t('pl.cabinet.availableProducts')}</p>
          <p className="text-4xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>{prods.loading ? '…' : available.length}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-600">{t('pl.cabinet.history')}</p>
          <LLink to="/profile" className="text-[#C41E3A] underline">{t('pl.cabinet.historyLink')}</LLink>
        </Card>
      </div>
      <section>
        <Heading level={2} className="text-2xl mb-4">{t('pl.cabinet.myProducts')}</Heading>
        {prods.loading ? <CardSkeletons count={2} /> : prods.error ? <ErrorState onRetry={prods.reload} /> : <ProductsGrid products={available} emptyText={t('pl.cabinet.myProductsEmptyText')} />}
      </section>
      <section>
        <div className="flex items-center justify-between mb-4"><Heading level={2} className="text-2xl">{t('pl.cabinet.newsTitle')}</Heading><LLink to="/cabinet/news" className="underline">{t('pl.cabinet.allNews')}</LLink></div>
        {news.loading ? <Spinner /> : news.error ? <ErrorState onRetry={news.reload} /> : news.data.length === 0
          ? <EmptyState icon={Newspaper} title={t('pl.cabinet.noNews')} />
          : <div className="grid gap-4 md:grid-cols-3">{news.data.slice(0, 3).map((n) => (
              <Card key={n.id}><p className="text-xs text-gray-500 mb-1">{formatDate(n.published_at, lang)}</p><LLink to={`/news/${n.slug}`} className="font-bold hover:text-[#C41E3A]">{n.title}</LLink></Card>))}</div>}
      </section>
    </div>
  );
};

const MyProducts = () => {
  const { t, i18n } = useTranslation();
  const { data, loading, error, reload } = useAsync(platformApi.products, [i18n.language]);
  if (loading) return <CardSkeletons />;
  if (error) return <ErrorState onRetry={reload} />;
  const granted = data.filter((p) => p.access?.state === 'granted');
  const rest = data.filter((p) => p.access?.state !== 'granted');
  return (
    <div className="space-y-10">
      <section>
        <Heading level={1} className="text-3xl mb-4">{t('pl.cabinet.myProducts')}</Heading>
        <ProductsGrid products={granted} emptyText={t('pl.cabinet.myProductsEmptyText2')} />
      </section>
      {rest.length > 0 && (
        <section>
          <Heading level={2} className="text-2xl mb-4">{t('pl.cabinet.locked')}</Heading>
          <p className="text-gray-600 mb-4">{t('pl.cabinet.lockedText')}</p>
          <ProductsGrid products={rest} />
        </section>
      )}
    </div>
  );
};

const CabinetCatalog = () => {
  const { t, i18n } = useTranslation();
  const { data, loading, error, reload } = useAsync(platformApi.products, [i18n.language]);
  return (<div><Heading level={1} className="text-3xl mb-4">{t('pl.catalog.pageTitle')}</Heading>
    {loading ? <CardSkeletons /> : error ? <ErrorState onRetry={reload} /> : <ProductsGrid products={data} emptyTitle={t('pl.catalog.emptyTitle')} emptyText={t('pl.cabinet.catalogEmpty')} />}</div>);
};

export const CourseCard = ({ c }) => {
  const { t } = useTranslation();
  const { lang } = useLang();
  return (
    <Card className="flex flex-col">
      {c.cover_url && <img src={c.cover_url} alt="" loading="lazy" className="w-full h-36 object-cover mb-3" />}
      <div className="flex gap-2 mb-2">
        <StatusBadge tone="blue">{c.kind === 'webinar' ? t('pl.courses.webinar') : t('pl.courses.course')}</StatusBadge>
        {c.is_free ? <StatusBadge tone="green">{t('pl.common.free')}</StatusBadge> : c.has_access ? <StatusBadge tone="green">{t('pl.courses.accessOpen')}</StatusBadge> : <StatusBadge tone="yellow">{formatPrice(c.price, lang)}</StatusBadge>}
      </div>
      <LLink to={`/webinars/${c.slug}`} className="hover:text-[#C41E3A]"><h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{c.title}</h3></LLink>
      <p className="text-gray-700 flex-1 mb-3">{c.summary}</p>
      <p className="text-sm text-gray-500 mb-3">
        {t('pl.courses.lessonsCount', { count: c.lesson_count, unit: plural(c.lesson_count, t('pl.units.lesson', { returnObjects: true })) })}{c.starts_at ? ` · ${formatDate(c.starts_at, lang)}` : ''}
      </p>
      <Btn to={`/webinars/${c.slug}`} variant={c.has_access ? 'primary' : 'secondary'}>{c.has_access ? t('pl.common.open') : t('pl.common.details')}</Btn>
    </Card>
  );
};

const CabinetCourses = () => {
  const { t, i18n } = useTranslation();
  const mine = useAsync(platformApi.myCourses, [i18n.language]);
  const all = useAsync(platformApi.courses, [i18n.language]);
  return (
    <div className="space-y-10">
      <section>
        <Heading level={1} className="text-3xl mb-4">{t('pl.cabinet.myCourses')}</Heading>
        {mine.loading ? <CardSkeletons count={2} /> : mine.error ? <ErrorState onRetry={mine.reload} /> : mine.data.length === 0
          ? <EmptyState icon={GraduationCap} title={t('pl.cabinet.noMyCourses')} action={<Btn to="/webinars">{t('pl.cabinet.allCourses')}</Btn>} />
          : <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">{mine.data.map((c) => <CourseCard key={c.id} c={c} />)}</div>}
      </section>
      {!all.loading && !all.error && all.data.length > 0 && (
        <section>
          <Heading level={2} className="text-2xl mb-4">{t('pl.cabinet.catalogTitle')}</Heading>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">{all.data.map((c) => <CourseCard key={c.id} c={c} />)}</div>
        </section>
      )}
    </div>
  );
};

const CabinetNews = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useLang();
  const { data, loading, error, reload } = useAsync(platformApi.news, [i18n.language]);
  return (
    <div><Heading level={1} className="text-3xl mb-4">{t('pl.newsPage.title')}</Heading>
      {loading ? <Spinner /> : error ? <ErrorState onRetry={reload} /> : data.length === 0 ? <EmptyState icon={Newspaper} title={t('pl.cabinet.noNews')} />
        : <div className="space-y-4">{data.map((n) => (
          <Card key={n.id}><p className="text-xs text-gray-500">{formatDate(n.published_at, lang)}</p>
            <LLink to={`/news/${n.slug}`} className="text-xl font-bold hover:text-[#C41E3A]">{n.title}</LLink>
            {n.summary && <p className="text-gray-700 mt-1">{n.summary}</p>}</Card>))}</div>}
    </div>
  );
};

const STATUS_TONE = { completed: 'green', pending: 'yellow', failed: 'red', cancelled: 'gray' };

const Purchases = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useLang();
  const { data, loading, error, reload } = useAsync(subscriptionAPI.getMyOrders, [i18n.language]);
  return (
    <div>
      <Heading level={1} className="text-3xl mb-4">{t('pl.cabinet.purchasesTitle')}</Heading>
      {loading ? <Spinner /> : error ? <ErrorState onRetry={reload} /> : data.length === 0
        ? <EmptyState icon={Receipt} title={t('pl.cabinet.purchasesEmpty')} text={t('pl.cabinet.purchasesEmptyText')} action={<Btn to="/catalog">{t('pl.cabinet.chooseTool')}</Btn>} />
        : (
          <div className="overflow-x-auto border-2 border-black bg-[#F5F1E8]">
            <table className="w-full text-left text-sm">
              <thead className="bg-black text-white"><tr>
                <th className="p-3">{t('pl.cabinet.cols.id')}</th><th className="p-3">{t('pl.cabinet.cols.date')}</th><th className="p-3">{t('pl.cabinet.cols.what')}</th><th className="p-3">{t('pl.cabinet.cols.amount')}</th><th className="p-3">{t('pl.cabinet.cols.status')}</th>
              </tr></thead>
              <tbody>{data.map((o) => (
                <tr key={o.id} className="border-t border-black/20">
                  <td className="p-3">{o.id}</td><td className="p-3 whitespace-nowrap">{formatDateTime(o.created_at, lang)}</td>
                  <td className="p-3">{o.title}{o.tokens_granted > 0 ? ` (+${o.tokens_granted})` : ''}</td>
                  <td className="p-3 whitespace-nowrap">{formatPrice(o.amount, lang)}</td>
                  <td className="p-3"><StatusBadge tone={STATUS_TONE[o.status] || 'gray'}>{t(`pl.cabinet.orderStatus.${o.status}`, { defaultValue: o.status })}</StatusBadge>{o.status === 'pending' && <LLink className="ml-2 underline" to={`/payment/result?order_id=${o.id}`}>{t('pl.cabinet.check')}</LLink>}</td>
                </tr>))}</tbody>
            </table>
          </div>
        )}
    </div>
  );
};

const ProfileSettings = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { lang } = useLang();
  return (
    <div className="max-w-2xl space-y-6">
      <Heading level={1} className="text-3xl">{t('pl.cabinet.settingsTitle')}</Heading>
      <Card>
        <dl className="grid grid-cols-[auto,1fr] gap-x-6 gap-y-3">
          <dt className="text-gray-600">{t('pl.cabinet.name')}</dt><dd className="font-bold">{user?.name}</dd>
          <dt className="text-gray-600">{t('pl.cabinet.email')}</dt><dd className="font-bold break-all">{user?.email}</dd>
          <dt className="text-gray-600">{t('pl.cabinet.registered')}</dt><dd>{formatDate(user?.created_at, lang)}</dd>
          <dt className="text-gray-600">{t('pl.cabinet.petrovich')}</dt><dd>{user?.is_admin || user?.petrovich_access ? t('pl.cabinet.petrovichYes') : t('pl.cabinet.petrovichNo')}</dd>
        </dl>
      </Card>
      <Card>
        <p className="mb-3 text-gray-700">{t('pl.cabinet.passwordText')}</p>
        <div className="flex flex-wrap gap-3">
          <Btn to="/profile" variant="secondary">{t('pl.cabinet.savedModels')}</Btn>
          <Btn variant="secondary" onClick={() => { logout(); window.location.assign('/'); }}><LogOut size={16} />{t('pl.cabinet.logout')}</Btn>
        </div>
      </Card>
    </div>
  );
};

/* ---------- Каркас ---------- */

const CabinetPage = () => {
  const { user, loading, checkAuth } = useAuth();
  const { t } = useTranslation();
  const { to } = useLang();
  const [open, setOpen] = useState(false);
  useEffect(() => { checkAuth(); }, []);
  if (loading) return <div className="min-h-screen paper-texture"><Spinner /></div>;
  if (!user) return <Navigate to={to('/login')} replace />;
  return (
    <div className="min-h-screen paper-texture">
      <SiteHeader />
      <Container className="py-8">
        <button className="lg:hidden mb-4 flex items-center gap-2 border-2 border-black px-3 py-2" onClick={() => setOpen((v) => !v)}>
          {open ? <X size={18} /> : <Menu size={18} />}{t('pl.cabinet.nav.sections')}
        </button>
        <div className="grid lg:grid-cols-[260px,1fr] gap-8">
          <aside className={`${open ? 'block' : 'hidden'} lg:block`}>
            <div className="lg:sticky lg:top-24 bg-[#F5F1E8] border-2 border-black p-3"><Sidebar user={user} onNavigate={() => setOpen(false)} /></div>
          </aside>
          <section className="min-w-0">
            <Routes>
              <Route index element={<CabinetHome />} />
              <Route path="products" element={<MyProducts />} />
              <Route path="catalog" element={<CabinetCatalog />} />
              <Route path="courses" element={<CabinetCourses />} />
              <Route path="news" element={<CabinetNews />} />
              <Route path="purchases" element={<Purchases />} />
              <Route path="settings" element={<ProfileSettings />} />
              <Route path="*" element={<Navigate to={to('/cabinet')} replace />} />
            </Routes>
          </section>
        </div>
      </Container>
    </div>
  );
};

export default CabinetPage;
